// React and React Native Core imports
import { useMemo } from 'react';

// 3rd party libraries
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useFormik, FormikProvider } from 'formik';
import { toast } from 'react-toastify';

// Our Own imports
import Button from '../../atoms/Button';
import { ComboboxSelfLoadingField, ComboboxField } from '../../atoms/Combobox';

// Relative path imports
import './AddOrgAccessForm.scss';

const GET_ROLES_QUERY = gql`
  query AddOrgAccessForm_GetRoles($orgId: uuid!) {
    roles(where: { _or: [{ organization_id: { _eq: $orgId } }, { organization_id: { _is_null: true } }] }) {
      id
      name
      privileges
    }
  }
`;

const UPSERT_USER_ORGANIZATION_MUTATION = gql`
  mutation AddOrgAccessForm_UpsertUserOrganization($object: user_organization_insert_input!) {
    insert_user_organization_one(
      object: $object
      on_conflict: { constraint: user_organization_pkey, update_columns: [deleted_at] }
    ) {
      user_id
      organization_id
      role_id
      deleted_at
    }
  }
`;

const handleSuccess = (orgName, roleName) =>
  toast.success(`Successfully added as ${roleName} role to ${orgName}`, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

const handleError = (error) => {
  let errorMessage = 'An error has occurred while adding organization access. Please try again.';

  console.error('Error adding organization access:', error);

  if (error?.cause?.extensions?.code === 'constraint-violation' && error?.message?.includes('user_organization_pkey')) {
    // not sentry capturing this one as it's an expected error if someone selects something incorrectly
    errorMessage = `A user cannot be added with the same organization + role more than once.`;
  } else {
    Sentry?.captureException(error);
  }
  toast.error(
    <div>
      <h4>Error</h4>
      <p>{errorMessage}</p>
    </div>,
    {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    },
  );
};

export default function AddOrgAccessForm({ userId, orgId = null, onSuccess = () => {}, userOrganizations = [] }) {
  // --- State hooks ---
  const formik = useFormik({
    initialValues: {
      org_id: orgId || '',
      role_id: '',
      // names just used for UI feedback
      org_name: '',
      role_name: '',
    },
    onSubmit: async (values, formikBag) => {
      if (!values.org_id || !values.role_id) {
        toast.error('Please select both an organization and a role');
        return;
      }

      try {
        const { data } = await upsertUserOrganization({
          variables: {
            object: {
              user_id: userId,
              organization_id: values.org_id,
              role_id: values.role_id,
              deleted_at: null, // Ensure deleted_at is set to null for upsert
            },
          },
        });

        if (data?.insert_user_organization_one) {
          handleSuccess(values.org_name, values.role_name);
          onSuccess(data.insert_user_organization_one);
          formikBag.resetForm();
        } else {
          handleError(data);
        }
      } catch (error) {
        console.error('Error adding organization access:', error);
        handleError(error);
      }
    },
  });

  // --- Apollo/GraphQL hooks ---
  const [upsertUserOrganization, { loading: saving }] = useMutation(UPSERT_USER_ORGANIZATION_MUTATION, {
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
  });
  const {
    data: rolesData,
    loading: rolesLoading,
    error: rolesError,
  } = useQuery(GET_ROLES_QUERY, {
    variables: { orgId: formik.values.org_id },
    skip: !formik.values.org_id,
  });

  // --- Derived state ---
  const roles = rolesData?.roles || [];

  // Create a map of organization IDs to existing role IDs (only for active entries)
  const existingActiveOrgRoles = useMemo(() => {
    const orgRolesMap = {};
    userOrganizations.forEach((userOrg) => {
      if (userOrg.organization?.id && userOrg.role?.id && !userOrg.deleted_at) {
        if (!orgRolesMap[userOrg.organization.id]) {
          orgRolesMap[userOrg.organization.id] = [];
        }
        orgRolesMap[userOrg.organization.id].push(userOrg.role.id);
      }
    });
    return orgRolesMap;
  }, [userOrganizations]);

  // Get existing active role IDs for the selected organization
  const existingActiveRoleIds = existingActiveOrgRoles[formik.values.org_id] || [];

  // Filter roles and add appropriate labels
  const availableRoles = roles.map((role) => {
    const isActive = existingActiveRoleIds.includes(role.id);

    return {
      ...role,
      disabled: isActive, // Only disable if already active
      name: isActive ? `${role.name} (Already active)` : role.name,
    };
  });

  console.log('roles', 'data', rolesData, 'loading', rolesLoading, 'error', rolesError);

  return (
    <FormikProvider value={formik}>
      <form className="AddOrgAccessForm" onSubmit={formik.handleSubmit}>
        <div className="AddOrgAccessForm-row">
          {!orgId && (
            <ComboboxSelfLoadingField
              name="org_id"
              placeholder="Select Organization"
              label="Organization"
              disabled={formik.isSubmitting || saving}
              fieldTableName="organization"
              fieldHasuraName="name"
              className="AddOrgAccessForm-orgSelect"
              onChange={(selectedOrg) => {
                formik.setFieldValue('org_id', selectedOrg?.id || '');
                formik.setFieldValue('org_name', selectedOrg?.name || '');
                // Clear role when organization changes
                formik.setFieldValue('role_id', '');
                formik.setFieldValue('role_name', '');
              }}
            />
          )}

          <ComboboxField
            disabled={formik.isSubmitting || saving}
            name="role_id"
            placeholder="Select Role"
            label="Role"
            className="AddOrgAccessForm-roleSelect"
            onChange={(selectedRole) => {
              formik.setFieldValue('role_id', selectedRole?.id || '');
              formik.setFieldValue('role_name', selectedRole?.name || '');
            }}
            items={availableRoles.map((role) => ({
              id: role.id,
              name: role.name,
              value: role.id,
              disabled: role.disabled,
              listItemClassName: role.disabled ? 'Combobox-listItem-disabledText' : '',
            }))}
          />
          <Button
            disabled={formik.isSubmitting || saving || !formik.values.org_id || !formik.values.role_id}
            type="submit"
            variant="vivid"
            color="success"
            content="Grant Access"
            className="AddOrgAccessForm-submitButton"
          />
        </div>
      </form>
    </FormikProvider>
  );
}

AddOrgAccessForm.propTypes = {
  userId: PropTypes.string.isRequired,
  orgId: PropTypes.string,
  onSuccess: PropTypes.func,
  userOrganizations: PropTypes.array,
};
